import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { api, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useBrand } from "../lib/useBrand";
import { toast } from "sonner";
import { AuthModeTabs, MagicLinkForm } from "../components/MagicLinkForm";
import { SESSION_REASONS } from "../lib/useSessionGuard";
import { PasswordField } from "../components/PasswordField";
import { API_BASE } from "../lib/api";

const OAUTH_ERRORS = {
  cancelled: "Microsoft sign-in was cancelled.",
  state: "That Microsoft sign-in attempt expired. Please try again.",
  exchange: "Microsoft didn't complete the sign-in. Please try again.",
  token: "We couldn't verify the Microsoft sign-in. Please try again.",
  tenant: "That Microsoft organization isn't allowed to sign in here.",
  no_email: "Your Microsoft account has no email we can match.",
  no_account: "No account here matches that Microsoft email. Ask your administrator to invite you first.",
  disabled: "This account is disabled. Contact your administrator.",
};

export default function Login() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(params.get("mode") === "magic" ? "magic" : "password");
  const [forgot, setForgot] = useState(params.get("forgot") === "1");
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const reason = SESSION_REASONS[params.get("reason")];
  const oauthError = OAUTH_ERRORS[params.get("oauth_error")];
  const [providers, setProviders] = useState({ google: true, microsoft: false });
  useEffect(() => { api.get("/auth/providers").then((r) => setProviders(r.data)).catch(() => {}); }, []);
  const microsoft = () => { window.location.assign(`${API_BASE}/auth/microsoft/start`); };
  const brand = useBrand();
  const brandLogo = brand.login_logo || brand.logo;
  const brandName = brand.name || "POSTINGS INC";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email: form.email, password: form.password });
      setUser(data);
      toast.success(`Welcome, ${data.name}`);
      navigate(data.role === "employee" ? "/me" : data.role === "client" ? "/client" : "/dashboard", { replace: true });
    } catch (e2) {
      setError(apiErr(e2));
    } finally {
      setBusy(false);
    }
  };

  const sendReset = async (e) => {
    e.preventDefault();
    setForgotBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail || form.email });
      toast.success("If that email has an account, a reset link is on its way.");
      setForgot(false);
      setForgotEmail("");
    } catch (e2) {
      toast.error(apiErr(e2));
    } finally {
      setForgotBusy(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="admin-theme min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="login-brand-panel relative hidden lg:flex flex-col justify-between bg-[#0a0a0a] text-white p-12 overflow-hidden">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1622675363311-3e1904dc1885?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            mixBlendMode: "luminosity",
          }}
        />
        <div className="relative z-10 flex items-center gap-3">
          {brandLogo ? (
            <img src={brandLogo} alt={brandName} data-testid="login-brand-logo" className="h-32 w-auto max-w-[380px] object-contain bg-white p-3" />
          ) : (
            <div className="h-9 w-9 bg-white text-black flex items-center justify-center font-black">P</div>
          )}
          {(brand.name || !brandLogo) && (
            <span className="font-black tracking-tight text-2xl" data-testid="login-brand-name">{brandName}</span>
          )}
        </div>
        <div className="relative z-10">
          <h1 className="text-5xl xl:text-6xl font-black tracking-tight leading-[0.95]">
            Canadian
            <br />
            Payroll,
            <br />
            done right.
          </h1>
          <p className="mt-6 text-white/60 max-w-sm text-sm leading-relaxed">
            HR & payroll processing for your clients — CPP, EI and income tax
            computed per CRA T4127 formulas. Pay stubs and T4 slips included.
          </p>
        </div>
        <div className="relative z-10 font-mono text-[11px] text-white/40 uppercase tracking-widest">
          T4127 · January 2026 · Ontario
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center p-6 bg-[#fafafa]">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8" data-testid="Login_76_12">
            {brandLogo ? (
              <img src={brandLogo} alt={brandName} className="h-12 w-auto max-w-[200px] object-contain" />
            ) : (
              <div className="h-8 w-8 bg-black text-white flex items-center justify-center font-black">P</div>
            )}
            {(brand.name || !brandLogo) && (
              <span className="font-black tracking-tight">{brandName}</span>
            )}
          </div>
          <h2 className="text-2xl font-black tracking-tight">Sign in</h2>
          <p className="text-sm text-muted-foreground mt-1">Access the payroll console.</p>
          {reason && <p data-testid="session-reason" className="mt-4 text-sm border border-amber-300 bg-amber-50 px-3 py-2">{reason}</p>}
          {oauthError && <p data-testid="oauth-error" className="mt-4 text-sm text-destructive border border-destructive/30 bg-destructive/5 px-3 py-2">{oauthError}</p>}
          <AuthModeTabs mode={mode} setMode={setMode} />

          {mode === "magic" ? (
            <div className="mt-6">
              <MagicLinkForm email={form.email} setEmail={(v) => setForm({ ...form, email: v })} />
              <Link to="/employee/login?mode=magic" data-testid="employee-portal-link-magic" className="block mt-4 text-xs font-medium text-muted-foreground hover:text-black underline underline-offset-2">Employee? Go to the Employee portal →</Link>
            </div>
          ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1.5 w-full border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Password</label>
              <PasswordField testId="login-password-input" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-destructive border border-destructive/30 bg-destructive/5 px-3 py-2">
                {error}
              </div>
            )}

            <button
              data-testid="login-submit-button"
              disabled={busy}
              className="w-full bg-black text-white py-3 text-sm font-medium hover:bg-neutral-800 transition-colors duration-200 disabled:opacity-50"
            >
              {busy ? "Please wait…" : "Sign in"}
            </button>
            <div className="flex justify-between items-center">
              <Link to="/employee/login" data-testid="employee-portal-link" className="text-xs font-medium text-muted-foreground hover:text-black underline underline-offset-2">
                Employee? Go to the Employee portal →
              </Link>
              <button type="button" data-testid="forgot-password-link" onClick={() => { setForgot((v) => !v); setForgotEmail(form.email); }} className="text-xs text-muted-foreground hover:text-black underline underline-offset-2">
                Forgot password?
              </button>
            </div>
          </form>
          )}

          {forgot && mode === "password" && (
            <form onSubmit={sendReset} data-testid="forgot-form" className="mt-4 border border-border bg-white p-4 space-y-3">
              <p className="text-xs text-muted-foreground">Enter your account email and we'll send a link to reset your password.</p>
              <input data-testid="forgot-email-input" type="email" required value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="you@company.ca" className="w-full border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
              <button data-testid="forgot-submit" disabled={forgotBusy} className="w-full bg-black text-white py-2.5 text-sm font-medium hover:bg-neutral-800 transition-colors disabled:opacity-50">
                {forgotBusy ? "Sending…" : "Send reset link"}
              </button>
            </form>
          )}

          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-border" />
            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">or sign in with</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <div className={`grid gap-3 ${providers.microsoft ? "grid-cols-2" : "grid-cols-1"}`}>
            <button
              data-testid="google-login-button"
              onClick={google}
              className="border border-black bg-white py-3 text-sm font-medium flex items-center justify-center gap-2 hover:bg-black hover:text-white transition-colors duration-200"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="currentColor" d="M12 11v2.8h4c-.2 1-1.3 3-4 3-2.4 0-4.3-2-4.3-4.4S9.6 8 12 8c1.4 0 2.3.6 2.8 1l1.9-1.8C15.5 6 13.9 5.3 12 5.3c-3.7 0-6.7 3-6.7 6.7s3 6.7 6.7 6.7c3.9 0 6.4-2.7 6.4-6.5 0-.4 0-.8-.1-1.2H12z"/></svg>
              Google
            </button>
            {providers.microsoft && (
              <button
                data-testid="microsoft-login-button"
                onClick={microsoft}
                className="border border-black bg-white py-3 text-sm font-medium flex items-center justify-center gap-2 hover:bg-black hover:text-white transition-colors duration-200"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#F25022" d="M2 2h9.5v9.5H2z"/><path fill="#7FBA00" d="M12.5 2H22v9.5h-9.5z"/><path fill="#00A4EF" d="M2 12.5h9.5V22H2z"/><path fill="#FFB900" d="M12.5 12.5H22V22h-9.5z"/></svg>
                Microsoft
              </button>
            )}
          </div>

          <p className="mt-8 text-center text-xs text-muted-foreground">
            Accounts are provisioned by your Postings Inc administrator.
          </p>
        </div>
      </div>
    </div>
  );
}
